import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Grid, List, SlidersHorizontal } from 'lucide-react';
import ArticleCard from '../components/news/ArticleCard';
import { articleService, categoryService } from '../services/api';
import { useNewspaper } from '../context/AppContext';
import styles from './CategoryPage.module.css';

export default function CategoryPage() {
  const { slug } = useParams();
  const { categories } = useNewspaper();
  
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid | list
  const [sortBy, setSortBy] = useState('newest'); // newest | oldest | popular
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const ARTICLES_PER_PAGE = 12;

  useEffect(() => {
    loadCategory();
  }, [slug]);

  useEffect(() => {
    if (category) {
      loadArticles();
    }
  }, [category, page, sortBy]);

  const loadCategory = async () => {
    try {
      const cat = await categoryService.getBySlug(slug);
      setCategory(cat);
      setPage(1);
    } catch (error) {
      console.error('Error loading category:', error);
    }
  };

  const loadArticles = async () => {
    try {
      setLoading(true);
      
      const sortOptions = {
        newest: { sortBy: 'publishedAt', sortOrder: 'desc' },
        oldest: { sortBy: 'publishedAt', sortOrder: 'asc' },
        popular: { sortBy: 'views', sortOrder: 'desc' }
      };

      // const { data, pagination } = await articleService.getAll({
      //   categoryId: category.id,
      //   page,
      //   limit: ARTICLES_PER_PAGE,
      //   ...sortOptions[sortBy]
      // });

      // setArticles(data);
      // setTotalPages(pagination.totalPages);

      const response = await articleService.getAll({
        // categoryId: category.id,
        category: category.slug,
        page,
        limit: ARTICLES_PER_PAGE,
        ...sortOptions[sortBy]
      });

      setArticles(response.data);
      setTotalPages(response.totalPages);

    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setPage(1);
    setShowFilters(false);
  };

  const subcategories = categories.filter(c => 
    c.parentId === category?.id || 
    (category?.subcategories && category.subcategories.includes(c.slug))
  );

  if (!category && !loading) {
    return (
      <div className={styles.notFound}>
        <h1>Sección no encontrada</h1>
        <p>La sección que buscas no existe.</p>
        <Link to="/" className="btn btn-primary">Volver al inicio</Link>
      </div>
    );
  }

  // Destacar primer artículo
  const featuredArticle = articles[0];
  const remainingArticles = articles.slice(1);

  return (
    <main className={styles.categoryPage}>
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <div className="container">
          <Link to="/">Inicio</Link>
          <ChevronRight size={14} />
          <span>{category?.name}</span>
        </div>
      </nav>

      {/* Header de la sección */}
      {category && (
  <header className={styles.header}>
    <div className={`container ${styles.headerContent}`}>
      <span 
        className={styles.categoryChip}
        style={{ backgroundColor: category.color }}
      >
        {category.name}
      </span>
    </div>
  </header>
)}

      {/* Controles */}
      <div className={styles.controls}>
        <div className="container">
          <div className={styles.controlsInner}>
            <div className={styles.resultsCount}>
              {!loading && (
                <span>
                  {articles.length > 0 
                    ? `Mostrando ${(page - 1) * ARTICLES_PER_PAGE + 1}-${Math.min(page * ARTICLES_PER_PAGE, articles.length + (page - 1) * ARTICLES_PER_PAGE)} artículos`
                    : 'No hay artículos'}
                </span>
              )}
            </div>

            <div className={styles.controlsRight}>
              {/* Filtros */}
              <div className={styles.filterWrapper}>
                <button 
                  className={styles.filterBtn}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <SlidersHorizontal size={16} />
                  Ordenar
                </button>

                {showFilters && (
                  <div className={styles.filterDropdown}>
                    <button 
                      className={sortBy === 'newest' ? styles.active : ''}
                      onClick={() => handleSortChange('newest')}
                    >
                      Más recientes
                    </button>
                    <button 
                      className={sortBy === 'oldest' ? styles.active : ''}
                      onClick={() => handleSortChange('oldest')}
                    >
                      Más antiguos
                    </button>
                    <button 
                      className={sortBy === 'popular' ? styles.active : ''}
                      onClick={() => handleSortChange('popular')}
                    >
                      Más leídos
                    </button>
                  </div>
                )}
              </div>

              {/* Modo de vista */}
              <div className={styles.viewToggle}>
                <button 
                  className={viewMode === 'grid' ? styles.active : ''}
                  onClick={() => setViewMode('grid')}
                  title="Vista en cuadrícula"
                >
                  <Grid size={18} />
                </button>
                <button 
                  className={viewMode === 'list' ? styles.active : ''}
                  onClick={() => setViewMode('list')}
                  title="Vista en lista"
                >
                  <List size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <section className={styles.content}>
        <div className="container">
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.loadingSpinner}></div>
              <p>Cargando artículos...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className={styles.empty}>
              <p>No hay artículos en esta sección</p>
            </div>
          ) : (
            <>
              {/* Artículo destacado (solo en primera página) */}
              {page === 1 && featuredArticle && (
                <div className={styles.featured}>
                  <ArticleCard 
                    article={featuredArticle}
                    variant="featured"
                    showExcerpt={true}
                    imageAspectRatio="21/9"
                  />
                </div>
              )}

              {/* Grid de artículos */}
              <div className={`${styles.articlesGrid} ${styles[viewMode]}`}>
                {(page === 1 ? remainingArticles : articles).map(article => (
                  <ArticleCard 
                    key={article.id}
                    article={article}
                    variant={viewMode === 'list' ? 'horizontal' : 'default'}
                    showExcerpt={viewMode === 'list'}
                  />
                ))}
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <nav className={styles.pagination}>
                  <button 
                    className={styles.pageBtn}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Anterior
                  </button>

                  <div className={styles.pageNumbers}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                      .map((p, index, arr) => (
                        <>
                          {index > 0 && arr[index - 1] !== p - 1 && (
                            <span key={`ellipsis-${p}`} className={styles.ellipsis}>...</span>
                          )}
                          <button
                            key={p}
                            className={`${styles.pageNumber} ${page === p ? styles.active : ''}`}
                            onClick={() => setPage(p)}
                          >
                            {p}
                          </button>
                        </>
                      ))
                    }
                  </div>

                  <button 
                    className={styles.pageBtn}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Siguiente
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </section>

      {/* Otras secciones */}
      <section className={styles.otherSections}>
        <div className="container">
          <h2 className={styles.otherTitle}>Explora otras secciones</h2>
          <div className={styles.otherGrid}>
            {categories
              .filter(c => c.slug !== slug && c.slug !== 'portada')
              .slice(0, 6)
              .map(cat => (
                <Link 
                  key={cat.id}
                  to={`/seccion/${cat.slug}`}
                  className={styles.otherCard}
                  style={{ '--cat-color': cat.color }}
                >
                  <span className={styles.otherIcon}>{cat.icon}</span>
                  <span className={styles.otherName}>{cat.name}</span>
                </Link>
              ))
            }
          </div>
        </div>
      </section>
    </main>
  );
}
